import gql from 'graphql-tag';

import {useBenchmarkProjectQuery} from '../generated/graphql';
import {captureError} from '../integrations';
import {BenchmarkProject} from '../types/graphql';

export const BENCHMARK_PROJECT_QUERY = gql`
  query BenchmarkProject($name: String!, $entityName: String) {
    project(name: $name, entityName: $entityName) {
      id
      access
      name
      description
      readOnly
      entityName
      views
      gitHubSubmissionRepo

      acceptedBenchmarkRuns: benchmarkRuns(state: "ACCEPTED") {
        ...benchmarkRunsFragment
      }
      rejectedBenchmarkRuns: benchmarkRuns(state: "REJECTED") {
        ...benchmarkRunsFragment
      }
      submittedBenchmarkRuns: benchmarkRuns(state: "SUBMITTED") {
        ...benchmarkRunsFragment
      }
      entity {
        id
        name
        photoUrl
        defaultAccess
        readOnlyAdmin
        privateOnly
        isTeam
      }
    }
  }

  fragment benchmarkRunsFragment on BenchmarkRunConnection {
    edges {
      node {
        id
        gitHubSubmissionPR
        createdAt
        details
        results
        state
        user {
          id
          username
          photoUrl
          admin
        }
        originalRun {
          id
          name
          displayName
          project {
            id
            name
            entityName
          }
        }
        originalProject {
          id
          name
          entityName
        }
        run {
          github
          commit
          id
          name
          displayName
          notes
          user {
            id
            username
          }
          diffPatchFile: files(first: 1, names: ["diff.patch"]) {
            edges {
              node {
                id
                name
                md5
                sizeBytes
              }
            }
          }
          summaryMetrics
        }
        benchmark {
          id
          name
          entityName
        }
      }
    }
  }
`;

// Any variables required by the query.
export interface Variables {
  entityName: string;
  name: string;
}

// The query's output shape.
interface Result {
  // We use projects as groups of runs on the backend.
  // Benchmark are implemented as a project.
  project: BenchmarkProject;
}

///// HOC definition

// These are the extra props required by the HOC. All are passed through to your
// wrapped component.
interface InputProps {
  match: {
    params: {
      entityName: string;
      // A benchmark is represented by a project as a group of runs.
      projectName: string;
    };
  };
}

// We define two types of query results, one for the loading state and one for
// the loaded state. This way the consumer can safely check the loading prop once.
// If it's false project is guaranteed to be defined.
interface QueryResultLoadingProps {
  loading: true;
  benchmark: undefined;
}

interface QueryResultLoadedProps {
  loading: false;
  benchmark: BenchmarkProject;
}

// The props that will be injected into your component, as a result of the query.
// They are derived from the result of the query in the HOC.
export interface BenchmarkPageQueryResultProps {
  pageQuery: QueryResultLoadedProps | QueryResultLoadingProps;
}

export const useBenchmarkProjectQueryResult = (
  entityName: string,
  projectName: string
): BenchmarkPageQueryResultProps => {
  const {data: benchmarkData, loading: benchmarkLoading} =
    useBenchmarkProjectQuery({
      variables: {
        entityName,
        name: projectName,
      },
    });

  if (benchmarkLoading) {
    return {
      pageQuery: {
        benchmark: undefined,
        loading: true,
      },
    };
  }

  if (benchmarkData == null || benchmarkData.project == null) {
    captureError(
      'Unexpected Apollo error, loading: false, data: undefined',
      'withBenchmarkPageQuery',
      {extra: {benchmarkData}}
    );
    throw new Error('Unexpected Apollo error, loading: false, data: undefined');
  }

  return {
    pageQuery: {
      loading: false,
      benchmark: {
        ...benchmarkData.project,
        acceptedBenchmarkRuns: benchmarkData.project.acceptedBenchmarkRuns ?? {
          edges: [],
        },
        rejectedBenchmarkRuns: benchmarkData.project.rejectedBenchmarkRuns ?? {
          edges: [],
        },
        submittedBenchmarkRuns: benchmarkData.project
          .submittedBenchmarkRuns ?? {edges: []},
        entity: benchmarkData.project.entity ?? {
          id: '',
          name: '',
          photoUrl: '',
          defaultAccess: '',
          readOnlyAdmin: false,
          privateOnly: false,
          isTeam: false,
        },
      } as unknown as BenchmarkProject,
    },
  };
};
